import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { PaymentServiceWithVersionsResponseType } from '../../../../../api/types/payments/payment-template';
import { CheckBounceReasonDetailsEntity } from '../../../../../api/types/settings/bouncing-check-reasons/bouncing-check-reasons.type';
import { PaymentServicesTable } from '../../../../payments/tabs/componenets/payment-services-table';
import { updateCheckBounceReasonOperation } from '../operations/update-check-bounce-reason-operation';

const BOUNCE_REASON_MAX_LENGTH = 20;

const resolvePaymentServiceVersionId = (
  service: PaymentServiceWithVersionsResponseType,
): number | null => {
  if (service.versionId) {
    return service.versionId;
  }
  if (service.versions && service.versions.length > 0) {
    const latestVersion = service.versions.reduce((latest, current) =>
      current.id > latest.id ? current : latest,
    );
    return latestVersion.id;
  }
  return null;
};

// Build a stub PaymentServiceWithVersionsResponseType from the row that owns
// the bounce reason. The PaymentServicesTable highlights selection by `id`
// (dataKey), so this is enough to visually pre-select the row when it appears
// on the current page; the resolver returns the original versionId because we
// set it explicitly here.
const buildInitialServiceStub = (
  row: CheckBounceReasonDetailsEntity,
): PaymentServiceWithVersionsResponseType => ({
  id: row.paymentServiceId,
  paymentServiceName: row.paymentServiceName,
  paymentServiceTypeId: row.paymentServiceTypeId,
  paymentServiceTypeName: row.paymentServiceTypeName,
  versionId: row.paymentServiceVersionId,
  versions: [],
  createdAt: row.createdAt,
  createdBy: row.createdBy,
  createdByName: row.createdByName,
});

interface EditCheckBounceReasonDialogProps {
  visible: boolean;
  onHide: () => void;
  onUpdated: () => void;
  row: CheckBounceReasonDetailsEntity | null;
}

export const EditCheckBounceReasonDialog: React.FC<
  EditCheckBounceReasonDialogProps
> = ({ visible, onHide, onUpdated, row }) => {
  const [bounceReason, setBounceReason] = useState<string>('');
  const [selectedService, setSelectedService] =
    useState<PaymentServiceWithVersionsResponseType | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Bumping this key remounts the shared PaymentServicesTable so its internal
  // filter / pagination state start fresh every time the dialog reopens.
  const [tableKey, setTableKey] = useState<number>(0);

  useEffect(() => {
    if (visible && row) {
      setBounceReason(row.bounceReason ?? '');
      setSelectedService(buildInitialServiceStub(row));
      setTableKey((k) => k + 1);
    }
  }, [visible, row]);

  const trimmedReason = bounceReason.trim();
  const isReasonValid =
    trimmedReason.length >= 1 &&
    trimmedReason.length <= BOUNCE_REASON_MAX_LENGTH;

  const resolvedVersionId = selectedService
    ? resolvePaymentServiceVersionId(selectedService)
    : null;

  const isApproveDisabled =
    !row || !selectedService || !isReasonValid || !resolvedVersionId || loading;

  const approveTooltip = !selectedService
    ? 'יש לבחור שירות תשלום'
    : !isReasonValid
      ? `יש להקליד סיבה (עד ${BOUNCE_REASON_MAX_LENGTH} תווים)`
      : !resolvedVersionId
        ? 'לשרות התשלום שנבחר אין גרסת מחיר זמינה'
        : 'עדכון סיבת חזרה';

  const handleApprove = async () => {
    if (!row || !selectedService || !isReasonValid || !resolvedVersionId) {
      return;
    }

    await updateCheckBounceReasonOperation(
      row.id,
      {
        bounceReason: trimmedReason,
        paymentServiceId: selectedService.id,
        paymentServiceVersionId: resolvedVersionId,
      },
      setLoading,
      () => {
        onUpdated();
        onHide();
      },
    );
  };

  const handleHide = () => {
    if (loading) return;
    onHide();
  };

  return (
    <Dialog
      header="עריכת סיבת חזרה להמחאה"
      visible={visible}
      onHide={handleHide}
      style={{ width: '900px' }}
      closable={!loading}
      modal
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          direction: 'rtl',
        }}
      >
        <div>
          <label
            htmlFor="edit-bounce-reason-input"
            style={{ display: 'block', marginBottom: '8px' }}
          >
            סיבת חזרה:
          </label>
          <InputText
            id="edit-bounce-reason-input"
            value={bounceReason}
            onChange={(e) => setBounceReason(e.target.value)}
            maxLength={BOUNCE_REASON_MAX_LENGTH}
            placeholder={`עד ${BOUNCE_REASON_MAX_LENGTH} תווים`}
            disabled={loading}
            style={{ width: '100%' }}
          />
          <div
            style={{
              fontSize: '12px',
              color: '#6b7280',
              marginTop: '4px',
              textAlign: 'left',
            }}
          >
            {trimmedReason.length}/{BOUNCE_REASON_MAX_LENGTH}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px' }}>
            בחירת שירות תשלום:
          </label>
          {selectedService && (
            <div
              style={{
                fontSize: '13px',
                color: '#374151',
                marginBottom: '8px',
              }}
            >
              שירות נוכחי: <strong>{selectedService.paymentServiceName}</strong>
            </div>
          )}
          <PaymentServicesTable
            key={tableKey}
            selectionMode="single"
            selectedService={selectedService}
            onSingleSelectionChange={setSelectedService}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'left',
            marginTop: '8px',
            paddingBottom: '10px',
          }}
        >
          <Button
            label="ביטול"
            onClick={handleHide}
            severity="secondary"
            disabled={loading}
          />
          <div style={{ width: '10px' }} />
          <Button
            label="אישור"
            onClick={handleApprove}
            loading={loading}
            disabled={isApproveDisabled}
            tooltip={approveTooltip}
            tooltipOptions={{ showOnDisabled: true, position: 'top' }}
          />
        </div>
      </div>
    </Dialog>
  );
};
